import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Mail, MessageSquare, MapPin, Phone, Send, Check,
  Building2, Users, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';

const Contact = ({ isInternal = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    type: 'general',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setLoading(false);
    toast.success('Message sent! We\'ll get back to you soon.');
  };

  const inquiryTypes = [
    { id: 'general', label: 'General Inquiry', icon: MessageSquare },
    { id: 'enterprise', label: 'Enterprise Sales', icon: Building2 },
    { id: 'careers', label: 'Career Interest', icon: Users },
    { id: 'student', label: 'Student/Non-Profit', icon: GraduationCap },
  ];

  return (
    <div className={`min-h-screen bg-white ${isInternal ? 'pt-6' : ''}`} data-testid="contact-page">
      <SEOHead
        title="Contact CARTIEAI — Talk to the Founders"
        description="Questions about the platform, integrations, or your cloud bill? Talk to the CARTIEAI founders directly. We respond within 1 business day."
        canonical="https://cartieai.com/contact"
      />
      {/* Navigation - Only show for public page */}
      {!isInternal && (
      <nav className="fixed top-0 w-full z-50 bg-slate-900 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/icons/cartie-emblem.png" 
                alt="CARTIEAI" 
                className="w-10 h-10 object-contain"
              />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/about" className="text-sm text-slate-600 hover:text-white transition-colors">About</Link>
              <Link to="/blog" className="text-sm text-slate-600 hover:text-white transition-colors">Blog</Link>
              <Link to="/careers" className="text-sm text-slate-600 hover:text-white transition-colors">Careers</Link>
              <Link to="/contact" className="text-sm text-emerald-700 font-medium">Contact</Link>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>
      )}

      {/* Hero Section */}
      <section className={`${isInternal ? 'pt-6 pb-8' : 'pt-32 pb-8'} px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-500/10 text-emerald-700 border border-emerald-500/20 px-4 py-2">
            <Mail className="w-4 h-4 mr-2" />
            Get in Touch
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Let's Talk
          </h1>
          <p className="text-xl text-slate-600">
            Questions, feedback, or just want to say hi? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {submitted ? (
            <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-3xl p-12 text-center border border-emerald-500/20">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-emerald-500/30 to-teal-500/15 border border-emerald-400/40 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/40">
                <Check className="w-9 h-9 text-emerald-700" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-4">
                Message Received!
              </h2>
              <p className="text-slate-600 mb-8">
                Thanks for reaching out. We'll get back to you as soon as possible.
              </p>
              <Link to="/">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Back to Home
                </Button>
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-emerald-500/30 shadow-[0_24px_80px_-20px_rgba(16,185,129,0.18)]" data-testid="contact-form-card">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Inquiry Type */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-3">
                    What's this about?
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {inquiryTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, type: type.id })}
                        className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                          formData.type === type.id
                            ? 'border-emerald-500 bg-emerald-500/10 text-emerald-700 shadow-sm shadow-emerald-500/20'
                            : 'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/40 text-slate-700'
                        }`}
                      >
                        <type.icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{type.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Your Name
                  </label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12 bg-white border-2 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20"
                    data-testid="contact-name-input"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-12 bg-white border-2 border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:border-emerald-500 focus-visible:ring-emerald-500/20"
                    data-testid="contact-email-input"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-semibold text-slate-900 mb-2">
                    Your Message
                  </label>
                  <textarea
                    placeholder="Tell us what's on your mind..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    rows={5}
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white text-base shadow-lg shadow-emerald-500/30"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* Alternative Contact — vibrant dark interlude */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-slate-950 overflow-hidden">
        {/* Ambient color washes */}
        <div className="absolute top-0 left-1/4 w-[36rem] h-[36rem] -translate-y-1/2 bg-emerald-500/12 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[32rem] h-[32rem] translate-y-1/2 bg-cyan-500/10 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[24rem] h-[24rem] bg-amber-500/8 blur-[140px] rounded-full pointer-events-none" />

        <div className="relative max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-emerald-300 mb-3">Three ways to reach us</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Talk to a real human.</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm">Email, phone, or post — every channel is monitored by the founder. No support tier 1, no chatbot.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Email card — emerald */}
            <div
              className="group relative bg-slate-900 rounded-2xl p-8 border border-emerald-500/30 hover:border-emerald-400/60 hover:-translate-y-1 transition-all shadow-[0_0_40px_-12px_rgba(16,185,129,0.25)] hover:shadow-[0_0_60px_-8px_rgba(16,185,129,0.45)]"
              data-testid="contact-email-card"
            >
              <div className="w-14 h-14 rounded-xl bg-emerald-500 ring-1 ring-white/20 flex items-center justify-center mb-5 shadow-[0_0_24px_0_rgba(16,185,129,0.6)] group-hover:scale-105 transition-transform">
                <Mail className="w-6 h-6 text-white" strokeWidth={2.25} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Email us directly
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Replies within 4 business hours from the founder.
              </p>
              <a
                href="mailto:hello@cartieai.com"
                className="text-emerald-300 hover:text-emerald-200 font-mono font-semibold inline-flex items-center gap-1.5"
              >
                hello@cartieai.com
                <Send className="w-3.5 h-3.5 -rotate-12" />
              </a>
            </div>

            {/* Phone card — cyan */}
            <div
              className="group relative bg-slate-900 rounded-2xl p-8 border border-cyan-500/30 hover:border-cyan-400/60 hover:-translate-y-1 transition-all shadow-[0_0_40px_-12px_rgba(6,182,212,0.25)] hover:shadow-[0_0_60px_-8px_rgba(6,182,212,0.45)]"
              data-testid="contact-phone-card"
            >
              <div className="w-14 h-14 rounded-xl bg-cyan-500 ring-1 ring-white/20 flex items-center justify-center mb-5 shadow-[0_0_24px_0_rgba(6,182,212,0.6)] group-hover:scale-105 transition-transform">
                <Phone className="w-6 h-6 text-white" strokeWidth={2.25} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Call the founders
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Mon–Fri · 9am–6pm CT · voicemail any time.
              </p>
              <a
                href="tel:+15126217610"
                className="text-cyan-300 hover:text-cyan-200 font-mono font-semibold inline-flex items-center gap-1.5"
                data-testid="contact-phone-link"
              >
                +1 (512) 621-7610
                <Phone className="w-3.5 h-3.5" />
              </a>
            </div>

            {/* Address card — amber */}
            <div
              className="group relative bg-slate-900 rounded-2xl p-8 border border-amber-500/30 hover:border-amber-400/60 hover:-translate-y-1 transition-all shadow-[0_0_40px_-12px_rgba(245,158,11,0.25)] hover:shadow-[0_0_60px_-8px_rgba(245,158,11,0.45)]"
              data-testid="contact-address-card"
            >
              <div className="w-14 h-14 rounded-xl bg-amber-500 ring-1 ring-white/20 flex items-center justify-center mb-5 shadow-[0_0_24px_0_rgba(245,158,11,0.6)] group-hover:scale-105 transition-transform">
                <MapPin className="w-6 h-6 text-white" strokeWidth={2.25} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                Our roots
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Registered in Texas. Built across the heartland.
              </p>
              <p className="text-amber-300 font-mono font-semibold">
                CARTIEAI LLC · Texas, USA
              </p>
              <p className="text-slate-500 text-xs mt-1.5">
                Born in India · Rooted in Wichita, Kansas
              </p>
            </div>
          </div>

          {/* Enterprise procurement strip */}
          <div className="mt-10 rounded-2xl border border-white/10 bg-slate-900/80 backdrop-blur p-6 flex flex-col md:flex-row items-center justify-between gap-4" data-testid="contact-procurement-strip">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-violet-500 ring-1 ring-white/20 flex items-center justify-center shadow-[0_0_20px_0_rgba(139,92,246,0.55)] shrink-0">
                <Building2 className="w-5 h-5 text-white" strokeWidth={2.25} />
              </div>
              <div>
                <p className="text-white font-semibold">Enterprise procurement?</p>
                <p className="text-slate-400 text-xs mt-0.5">Skip the form — grab our pre-signed MSA + DPA templates and route them to your legal team.</p>
              </div>
            </div>
            <Link
              to="/legal/templates"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold shadow-[0_0_24px_-4px_rgba(139,92,246,0.6)] transition-all whitespace-nowrap"
              data-testid="contact-msa-dpa-cta"
            >
              View MSA + DPA templates
              <Send className="w-3.5 h-3.5 -rotate-45" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer - Only show for public page */}
      {!isInternal && (
      <footer className="py-8 bg-slate-950 text-slate-600 px-4 sm:px-6 lg:px-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-6 h-6" />
            <span className="text-sm">
              <span className="text-white">CARTIE<span className="text-emerald-700">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </span>
          </div>
          <p className="text-sm" data-testid="contact-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · All rights reserved.</p>
        </div>
      </footer>
      )}
    </div>
  );
};

export default Contact;
